/**
 * Admin Users Management Page
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { UserTable } from '../../components/admin/UserTable';
import api from '../../services/api';

export const Route = createFileRoute('/admin/users')({
  component: AdminUsers,
  validateSearch: (search: Record<string, unknown>): { q?: string } => (typeof search.q === 'string' && search.q ? { q: search.q } : {}),
});

export interface User {
  id: string;
  email: string;
  displayName: string | null;
  approved: boolean;
  role: string;
  createdAt: string;
  lastLoginAt: string | null;
}

function AdminUsers() {
  const { q } = Route.useSearch();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/approval/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (email: string) => {
    try {
      await api.post('/approval/approve', { email });
      // Reload users
      await loadUsers();
    } catch (error) {
      console.error('Failed to approve user:', error);
    }
  };

  const handleRevoke = async (email: string) => {
    try {
      await api.post('/approval/revoke', { email });
      // Reload users
      await loadUsers();
    } catch (error) {
      console.error('Failed to revoke user:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">User Management</h1>
        <p className="text-muted-foreground">
          Manage user accounts and permissions
        </p>
      </div>

      <UserTable
        users={users}
        initialSearch={q}
        onApprove={handleApprove}
        onRevoke={handleRevoke}
        onRefresh={loadUsers}
      />
    </div>
  );
}
